import React, { useEffect, useMemo } from "react";
import FeatherIcon from "feather-icons-react";
import CustomModal from "src/components/configuracion/components/customModal";
import "src/components/documentostributarios/ModalDeleteObligations.scss";
import { bulkUnmatchSelectedLabel, bulkUnmatchTargetNoun } from "./utils/bulkUnmatchLabels";

/**
 * Confirmación de la desconciliación masiva.
 *
 * Es un paso obligatorio, no una cortesía: desconciliar ELIMINA los asientos
 * contables generados por el match y eso no se puede deshacer. El preflight del
 * server dice exactamente cuántos, y ese número es lo primero que se muestra.
 */
export default function BulkUnmatchModal({
  open,
  onClose,
  onConfirm,
  /** Registros seleccionados: { type, ids } o { groups: [{ type, ids }] }. */
  selection,
  /** Resultado de previewBulkUnmatch. */
  preview,
  previewing,
  launching,
  runPreview,
  runPreviewGroups,
}) {
  const type = selection?.type;
  // El array de ids se recrea en cada render del padre, así que la identidad no
  // sirve como dependencia: se compara por contenido a través de esta clave.
  const idsKey = (selection?.ids || []).join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);
  const groupsKey = (selection?.groups || [])
    .map((group) => `${group.type}:${(group.ids || []).join(",")}`)
    .join("|");
  const groups = useMemo(
    () =>
      groupsKey
        ? groupsKey.split("|").map((part) => {
            const separator = part.indexOf(":");
            return {
              type: part.slice(0, separator),
              ids: part.slice(separator + 1).split(",").filter(Boolean),
            };
          })
        : [],
    [groupsKey],
  );

  // El preflight se corre al abrir: los datos tienen que ser del momento de
  // confirmar, no de cuando se marcó el checkbox.
  useEffect(() => {
    if (!open) return;
    if (groups.length > 1) {
      if (runPreviewGroups) runPreviewGroups(groups);
      return;
    }
    if (!type || ids.length === 0) return;
    runPreview({ type, ids });
  }, [open, type, ids, groups, runPreview, runPreviewGroups]);

  const summary = useMemo(() => {
    if (!preview) return [];
    return [
      { label: bulkUnmatchSelectedLabel(type), value: preview.targets, icon: "list" },
      { label: "Matches por deshacer", value: preview.totalMatches, icon: "link-2" },
      ...(preview.entriesToDelete > 0
        ? [
            {
              label: "Asientos contables que se eliminarán",
              value: preview.entriesToDelete,
              icon: "book-open",
              emphasis: true,
            },
          ]
        : []),
      ...(preview.obligationsToDelete > 0
        ? [
            {
              label: "Obligaciones que se eliminarán",
              value: preview.obligationsToDelete,
              icon: "file-minus",
              emphasis: true,
            },
          ]
        : []),
    ];
  }, [preview, type]);

  const blockers = preview?.blockers || [];
  const warnings = preview?.warnings || [];
  const canProceed = Boolean(preview?.canProceed) && !previewing && !launching;

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title="Deshacer matches"
      size="tiny"
      confirmLabel={
        preview?.totalMatches > 0
          ? `Deshacer ${preview.totalMatches} match(es)`
          : "Deshacer matches"
      }
      confirmDisabled={!canProceed}
      isLoading={launching}
      onConfirm={() =>
        onConfirm(groups.length > 1 ? { groups } : { type, ids })
      }
      content={
        <div className="delete-obligation-modal-body">
          <div className="delete-obligation-modal-icon" aria-hidden>
            <FeatherIcon icon="unlink" size={22} />
          </div>

          {previewing && (
            <p className="delete-obligation-modal-lead">
              Revisando qué se deshará…
            </p>
          )}

          {!previewing && (
            <>
              <p className="delete-obligation-modal-lead">
                Se desharán <strong>todos los matches</strong> de los{' '}
                {bulkUnmatchTargetNoun(type)} seleccionados.
              </p>

              {summary.length > 0 && (
                <div className="delete-obligation-modal-summary">
                  {summary.map((row) => (
                    <div key={row.label} className="delete-obligation-modal-summary-row">
                      <span className="tw-flex tw-items-center tw-gap-2">
                        <FeatherIcon icon={row.icon} size={14} />
                        {row.label}
                      </span>
                      <span
                        className="delete-obligation-modal-summary-value"
                        style={row.emphasis ? { color: "#d0021b" } : undefined}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {warnings.map((warning) => (
                <p key={warning} className="delete-obligation-modal-warning">
                  {warning}
                </p>
              ))}

              {blockers.length > 0 && (
                <div
                  className="tw-mt-3 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700"
                  role="alert"
                >
                  {blockers.map((blocker) => (
                    <p key={blocker} className="tw-mb-1 last:tw-mb-0">
                      {blocker}
                    </p>
                  ))}
                </div>
              )}

              {blockers.length === 0 && (
                <p className="delete-obligation-modal-warning">
                  El proceso corre en segundo plano: puedes seguir trabajando y te avisamos al
                  terminar. Los matches que estén en un período contable cerrado se omitirán.
                </p>
              )}
            </>
          )}
        </div>
      }
    />
  );
}
